import { useId, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { Dialog } from '@material-ui/core';
import clsx from 'clsx';

import { PdvCloseIcon } from '../icons';
import { useStyles } from './styles';

interface PdvDialogProps {
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  /** shown left of the title, e.g. the green "sent" badge */
  icon?: ReactNode;
  /** the button row; laid out right-aligned */
  actions?: ReactNode;
  children?: ReactNode;
  /** sm 600px · md 900px */
  size?: 'sm' | 'md';
  /** grey sheets hold white cards (the rep flow); white ones hold content directly */
  surface?: 'white' | 'grey';
}

/** The design system's modal: 20px corners, 24px padding, round close button. */
export const PdvDialog = ({
  onClose,
  title,
  subtitle,
  icon,
  actions,
  children,
  size = 'sm',
  surface = 'white'
}: PdvDialogProps) => {
  const classes = useStyles();
  const titleId = useId();

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        className: clsx(classes.sheet, classes[surface], classes[size]),
        'aria-labelledby': titleId
      }}>
      <button
        type="button"
        className={classes.close}
        aria-label="Close"
        onClick={onClose}>
        <PdvCloseIcon />
      </button>

      <div className={classes.heading}>
        <div className={classes.titleRow}>
          {icon}
          <h2 id={titleId} className={classes.title}>
            {title}
          </h2>
        </div>
        {subtitle && <p className={classes.subtitle}>{subtitle}</p>}
      </div>

      {children}

      {actions && <div className={classes.actions}>{actions}</div>}
    </Dialog>
  );
};

/** A white block inside a grey sheet. */
export const PdvDialogCard = ({ children }: { children: ReactNode }) => {
  const classes = useStyles();
  return <div className={classes.card}>{children}</div>;
};

/** A label and the field under it; the label names the field for assistive tech. */
export const PdvDialogField = ({
  label,
  children
}: {
  label: ReactNode;
  children: ReactNode;
}) => {
  const classes = useStyles();
  return (
    // The control arrives as `children`, which the rule can't see through.
    // eslint-disable-next-line jsx-a11y/label-has-associated-control
    <label className={classes.field}>
      <span className={classes.label}>{label}</span>
      {children}
    </label>
  );
};

export const PdvDialogLabel = ({ children }: { children: ReactNode }) => {
  const classes = useStyles();
  return <span className={classes.label}>{children}</span>;
};

export const PdvDialogBanner = ({
  tone = 'neutral',
  icon,
  children
}: {
  tone?: 'neutral' | 'positive' | 'warning';
  icon?: ReactNode;
  children: ReactNode;
}) => {
  const classes = useStyles();
  const toneClass = {
    neutral: classes.bannerNeutral,
    positive: classes.bannerPositive,
    warning: classes.bannerWarning
  }[tone];
  return (
    <div className={clsx(classes.banner, toneClass)}>
      {icon}
      {children}
    </div>
  );
};

export const PdvTextarea = ({
  className,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement>) => {
  const classes = useStyles();
  return <textarea className={clsx(classes.textarea, className)} {...rest} />;
};
